import { DNABox, DNAButton, DNADivider, DNAText, Iffy } from '@alucio/lux-ui'
import React from 'react'
import { FolderORM } from 'src/types/orms'
import UserRow from './UserRow'
import LabelRow from './LabelRow'
import { getRootFolder } from 'src/utils/foldersHelpers';
import { SharePermission, ShareTargetType } from '@alucio/aws-beacon-amplify/src/models';
import { useIndexedUsers } from 'src/state/redux/selector/user';
import { useDNAFolderActions } from 'src/components/DNA/Folder/DNAFolder.actions'

interface SharingProps {
  itemORM: FolderORM
}

const Sharing: React.FC<SharingProps> = (props) => {
  const { itemORM } = props
  const folderActions  = useDNAFolderActions();
  const name = itemORM.model.name
  const rootFolder = getRootFolder(itemORM);
  const itsSharing = rootFolder.model.sharePermissions?.some(({ isDeleted }) => !isDeleted);
  const indexedUsers = useIndexedUsers();
  const isSharedWithEveryone = rootFolder.model.sharePermissions?.some(({ targetType, isDeleted }) =>
    targetType === ShareTargetType.ALL && !isDeleted);

  const targetUserPermissions: SharePermission[] = [];
  const labelValuePermissions: SharePermission[] = [];

  // DIVIDES THE PERMISSIONS INTO 2 DIFFERENT ARRAYS TO LATER USE
  rootFolder?.model.sharePermissions?.forEach((permission) => {
    if (permission.isDeleted) {
      return;
    }

    if (permission.targetType === ShareTargetType.USER && indexedUsers[permission.targetUsername || '']) {
      targetUserPermissions.push(permission);
    } else if (permission.targetType === ShareTargetType.FILTER) {
      labelValuePermissions.push(permission);
    }
  });

  // ORDER THE USERS ALPHABETICALLY
  targetUserPermissions.sort((a, b) =>
    (indexedUsers[a.targetUsername!].meta.formattedName || '') >
    (indexedUsers[b.targetUsername!].meta.formattedName || '') ? 1 : -1,
  );

  return (
    <DNABox
      fill
      appearance="col"
      style={{ paddingHorizontal: 24 }}
      spacing="large"
      childStyle={[1, { flex: 1, overflow: 'scroll' }]}
    >
      <DNABox appearance="col">
        {/* SECTION - Header */}
        <DNABox style={{ minHeight: 64 }} alignY="center">
          <DNAText bold numberOfLines={2}>{name}</DNAText>
        </DNABox>
        {/* !SECTION - Header */}
        <DNADivider />

        {/* SECTION - Subheader */}
        <DNABox fill alignY="center" style={{ paddingVertical: 12, maxHeight: 56 }}>
          <DNABox>
            <DNAText h4>Sharing</DNAText>
          </DNABox>
          <Iffy is={!itemORM.relations.parentFolder}>
            <DNABox fill alignX="end">
              <DNAButton
                appearance="outline"
                status="subtle"
                size="small"
                onPress={folderActions.share(itemORM)}
              >
                <DNAText>Share</DNAText>
              </DNAButton>
            </DNABox>
          </Iffy>
        </DNABox>
        {/* !SECTION - Subheader */}
      </DNABox>

      {/* SECTION - SHARING PROPERTIES */}
      <Iffy is={itsSharing}>
        <DNABox appearance="col" fill spacing="small">
          {/* SECTION - SHARED WITH EVERYONE */}
          <Iffy is={isSharedWithEveryone}>
            <DNAText bold>Everyone</DNAText>
            <DNADivider />
          </Iffy>
          {/* SECTION - LABEL/VALUES PERMISSIONS */}
          {
              labelValuePermissions.map((permission) => (
                <DNABox appearance="col" key={permission.id} fill spacing="small">
                  {
                    permission.targetLabels?.map((labelValue) => (
                      <DNABox key={permission.id + labelValue.key} fill appearance="col">
                        <LabelRow label={labelValue.key} values={labelValue.values} />
                      </DNABox>
                    ))
                  }
                  <DNADivider />
                </DNABox>
              ))
            }
          {/* !SECTION - LABEL/VALUES PERMISSIONS */}
          {/* SECTION - TARGET USER PERMISSIONS */}
          {
              targetUserPermissions.map((permission) => (
                <DNABox key={permission.id} fill spacing="small" appearance="col">
                  <UserRow userID={permission.targetUsername!} />
                  <DNADivider />
                </DNABox>
              ))
            }
          {/* !SECTION - TARGET USER PERMISSIONS */}
        </DNABox>
      </Iffy>
      {/* !SECTION - SHARING PROPERTIES */}

      {/* SECTION - NOT SHARING MESSAGE */}
      <Iffy is={!itsSharing}>
        <DNAText status="subtle">Only you have access</DNAText>
      </Iffy>
      {/* !SECTION - NOT SHARING MESSAGE */}
    </DNABox>
  )
}

export default Sharing
